import { Fragment, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { fmtNum, fmtPrice } from "@/lib/api";
import { HorseBreakdown } from "@/components/race/HorseBreakdown";
import { HorseForm } from "@/components/race/HorseForm";

const COLS = [["No.", "w-7 sm:w-14"], ["Last Form", "hidden sm:table-cell"], ["Horse Name", ""], ["Barrier", "text-right"], ["Jockey", "hidden sm:table-cell"], ["Weight", "text-right"], ["Rating", "text-right"], ["Price", "text-right"]];
const P = "px-[3px] sm:px-4 py-2.5 sm:py-3";
const SHORT = { Barrier: "Bar", Weight: "Wgt" };
const wt = (h) => (h.weight != null && h.weight !== "" ? `${Number(h.weight)}` : "—");
const ADJ_COLS = [...COLS.slice(0, 3), ["Age", "text-right"], ...COLS.slice(3, 6), ["Adj", "text-right"], ...COLS.slice(6)];
const ageText = (h) => (h.age ? `${h.age}yo${h.sex ? ` ${h.sex[0].toUpperCase()}` : ""}` : "—");
const AgeCell = ({ h, testId }) => <td className={`${P} num text-right text-slate-300 whitespace-nowrap`} title={h.sex || undefined} data-testid={testId}>{ageText(h)}</td>;

const AdjInput = ({ value, onSave, testId }) => {
  const [v, setV] = useState(value ? String(value) : "");
  useEffect(() => { setV(value ? String(value) : ""); }, [value]);
  const commit = () => { const n = v === "" ? 0 : Number(v); if (!Number.isNaN(n) && n !== (value || 0)) onSave(n); };
  return (
    <input type="number" step="0.5" min={-100} max={100} value={v} placeholder="0" data-testid={testId}
      onClick={(e) => e.stopPropagation()} onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className={`no-print w-14 bg-[#0F172A] border rounded px-1.5 py-0.5 text-right num text-sm outline-none focus:border-emerald-500 ${value ? "border-amber-500/60 text-amber-300" : "border-slate-700 text-slate-300"}`} />
  );
};

const FirstStarterRow = ({ h, i, testId, adj }) => (
  <tr className="border-b border-slate-800/60 text-slate-400" data-testid={`${testId}-fs-row-${i}`}>
    <td className={`${P} num font-bold`}>{h.number ?? "-"}</td>
    <td className={`hidden sm:table-cell ${P} num text-amber-400/80 text-xs uppercase tracking-wider`}>1st starter</td>
    <td className={`${P} text-slate-300 font-semibold sm:whitespace-nowrap`} data-testid={`${testId}-fs-name-${i}`}>
      {h.name}<div className="sm:hidden text-xs font-normal text-amber-400/80 mt-0.5">1st starter{h.jockey ? ` • ${h.jockey}` : ""}</div>
    </td>
    {adj && <AgeCell h={h} testId={`${testId}-fs-age-${i}`} />}
    <td className={`${P} num text-right`}>{h.barrier != null ? fmtNum(h.barrier, 0) : "—"}</td>
    <td className={`hidden sm:table-cell ${P} whitespace-nowrap`}>{h.jockey || "—"}</td>
    <td className={`${P} num text-right`} data-testid={`${testId}-fs-weight-${i}`}>{wt(h)}</td>
    {adj && <td className={P} />}
    <td className={`${P} num text-right`} data-testid={`${testId}-fs-rating-${i}`}>0.0</td>
    <td className={`${P} num text-right sm:text-lg`} data-testid={`${testId}-fs-price-${i}`}>$0.00</td>
  </tr>
);

const ScratchedRow = ({ h, i, testId, adj }) => (
  <tr className="border-b border-slate-800/60 text-slate-500" data-testid={`${testId}-scr-row-${i}`}>
    <td className={`${P} num line-through`}>{h.number ?? "-"}</td>
    <td className={`hidden sm:table-cell ${P} num text-rose-400/80 text-xs uppercase tracking-wider`}>Scratched</td>
    <td className={`${P} sm:whitespace-nowrap`} data-testid={`${testId}-scr-name-${i}`}>
      <span className="line-through">{h.name}</span><span className="sm:hidden ml-2 text-xs text-rose-400/80">SCR</span>
    </td>
    {adj && <AgeCell h={h} testId={`${testId}-scr-age-${i}`} />}
    <td className={`${P} num text-right line-through`}>{h.barrier != null ? fmtNum(h.barrier, 0) : "—"}</td>
    <td className={`hidden sm:table-cell ${P} whitespace-nowrap`}>—</td>
    <td className={`${P} num text-right line-through`}>{wt(h)}</td>
    {adj && <td className={P} />}
    <td className={`${P} num text-right text-rose-400/80 text-xs`} colSpan={2}>SCR</td>
  </tr>
);

export const FinalSheet = ({ race, ratings, sort = "rating", testId = "final-sheet", breakdown = false, form = false, expandAll = false, firstStarters = [], scratched = [], onAdjust }) => {
  const clickable = breakdown || form;
  const [openName, setOpenName] = useState(null);
  const [closed, setClosed] = useState([]);
  const byName = Object.fromEntries((race.horses || []).map((h) => [h.name, h]));
  const cols = onAdjust ? ADJ_COLS : COLS;
  const adj = Boolean(onAdjust);
  const rows = sort === "number" ? [...ratings].sort((a, b) => (a.number ?? 99) - (b.number ?? 99)) : ratings;
  return (
    <div className="scroll-x">
      <table className="w-full text-xs sm:text-base" data-testid={`${testId}-table`}>
        <thead>
          <tr className="eyebrow border-b border-slate-700">
            {cols.map(([l, c]) => (
              <th key={l} className={`${P} font-normal text-left ${c}`}>
                {SHORT[l] ? <><span className="sm:hidden">{SHORT[l]}</span><span className="hidden sm:inline">{l}</span></> : l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const h = byName[r.name] || {};
            const isOpen = clickable && (expandAll ? !closed.includes(r.name) : openName === r.name);
            return (
              <Fragment key={r.name}>
              <tr data-testid={`${testId}-row-${i}`} onClick={clickable ? () => (expandAll ? setClosed(isOpen ? [...closed, r.name] : closed.filter((n) => n !== r.name)) : setOpenName(isOpen ? null : r.name)) : undefined}
                className={`row-hover border-b border-slate-800/60 ${r.rank === 1 ? "bg-emerald-500/[0.07]" : ""} ${clickable ? "cursor-pointer" : ""}`}>
                <td className={`${P} num font-bold text-emerald-400`} data-testid={`${testId}-number-${i}`}>{r.number ?? "-"}</td>
                <td className={`hidden sm:table-cell ${P} num tracking-wider text-cyan-300`} data-testid={`${testId}-form-${i}`}>{h.form || "—"}</td>
                <td className={`${P} text-white font-semibold sm:whitespace-nowrap`} data-testid={`${testId}-name-${i}`}>
                  {r.name}
                  {h.emergency && <span className="ml-1.5 text-[10px] font-mono font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40" title="Emergency" data-testid={`${testId}-emergency-${i}`}>EM</span>}
                  {clickable && <ChevronDown className={`no-print inline h-4 w-4 ml-1.5 text-emerald-400/70 transition-transform ${isOpen ? "rotate-180" : ""}`} data-testid={`${testId}-breakdown-toggle-${i}`} />}
                  <div className="sm:hidden text-xs font-normal text-slate-400 mt-0.5"><span className="num text-cyan-300 mr-2">{h.form}</span>{h.jockey}</div>
                </td>
                {adj && <AgeCell h={h} testId={`${testId}-age-${i}`} />}
                <td className={`${P} num text-right`} data-testid={`${testId}-barrier-${i}`}>{h.barrier != null ? fmtNum(h.barrier, 0) : "—"}</td>
                <td className={`hidden sm:table-cell ${P} text-slate-300 whitespace-nowrap`} data-testid={`${testId}-jockey-${i}`}>{h.jockey || "—"}</td>
                <td className={`${P} num text-right text-slate-300`} data-testid={`${testId}-weight-${i}`}>{wt(h)}</td>
                {adj && <td className={`${P} text-right`}><AdjInput value={r.adjustment} onSave={(n) => onAdjust(r.number, n)} testId={`${testId}-adj-${i}`} /></td>}
                <td className={`${P} num text-right font-bold text-emerald-400`} data-testid={`${testId}-rating-${i}`}>{r.rating == null ? <span className="text-slate-600">—</span> : Number(r.rating).toFixed(1)}</td>
                <td className={`${P} num text-right font-bold text-amber-400 sm:text-lg`} data-testid={`${testId}-price-${i}`}>{r.price == null ? <span className="text-slate-600">—</span> : fmtPrice(r.price)}</td>
              </tr>
              {isOpen && <tr className="no-print"><td colSpan={cols.length} className="px-1.5 sm:px-4 pb-3">{form ? <div className="w-0 min-w-full"><HorseForm raceId={race.id} number={r.number} testId={`${testId}-horse-form-${i}`} /></div> : <HorseBreakdown r={r} testId={`${testId}-breakdown-${i}`} />}</td></tr>}
              </Fragment>
            );
          })}
          {[...(firstStarters || [])].sort((a, b) => (a.number ?? 99) - (b.number ?? 99)).map((h, i) => <FirstStarterRow key={`fs-${h.name}`} h={h} i={i} testId={testId} adj={adj} />)}
          {scratched.length > 0 && (
            <tr data-testid={`${testId}-scr-heading`}><td colSpan={cols.length} className="px-1.5 sm:px-4 pt-3 pb-1 eyebrow text-rose-400/80">Scratchings ({scratched.length})</td></tr>
          )}
          {[...scratched].sort((a, b) => (a.number ?? 99) - (b.number ?? 99)).map((h, i) => <ScratchedRow key={`scr-${h.name}`} h={h} i={i} testId={testId} adj={adj} />)}
        </tbody>
      </table>
    </div>
  );
};

export const SortToggle = ({ value, onChange, testId }) => (
  <div className="inline-flex bg-[#0F172A] border border-slate-800 rounded p-0.5 no-print" data-testid={testId}>
    {[["number", "Sort by No."], ["rating", "Sort by Rating"]].map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
        className={`px-3 py-1.5 rounded text-xs font-mono transition-colors ${value === k ? "bg-emerald-500/15 text-emerald-300" : "text-slate-400 hover:text-white"}`}>{l}</button>
    ))}
  </div>
);

export const FirstStarters = ({ list, testId }) => {
  if (!list?.length) return null;
  return (
    <div className="mt-3" data-testid={testId}>
      <div className="eyebrow text-amber-400 mb-1.5">First Starters - No Exposed Form</div>
      <table className="w-full text-sm">
        <tbody>
          {list.map((h, i) => (
            <tr key={h.name} className="border-b border-slate-800/60" data-testid={`${testId}-row-${i}`}>
              <td className="px-1.5 sm:px-4 py-1.5 num text-slate-400 w-10 sm:w-14">{h.number ?? "-"}</td>
              <td className="px-1.5 sm:px-4 py-1.5 text-slate-200">{h.name}</td>
              <td className="px-1.5 sm:px-4 py-1.5 num text-right text-slate-400">{h.barrier != null ? `Bar ${fmtNum(h.barrier, 0)}` : ""}</td>
              <td className="px-1.5 sm:px-4 py-1.5 text-slate-400 hidden sm:table-cell">{h.jockey || ""}</td>
              <td className="px-1.5 sm:px-4 py-1.5 num text-right text-slate-400" data-testid={`${testId}-weight-${i}`}>{h.weight != null && h.weight !== "" ? `${Number(h.weight)}kg` : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
